import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Truck, Loader2, Globe, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const { t, language, toggleLanguage } = useLanguage();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await login(email, password);
            toast.success(t('messages.loginSuccess'));
            navigate('/app/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || t('messages.loginError'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-container">
            {/* Language Toggle */}
            <button
                onClick={toggleLanguage}
                className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 hover:border-[#1B7EB5] text-slate-600 hover:text-[#1B7EB5] bg-white transition-colors"
                data-testid="login-language-toggle"
            >
                <Globe className="w-4 h-4" />
                <span className="text-sm font-medium">{language === 'en' ? 'EN' : 'ΕΛ'}</span>
            </button>
            
            <div className="auth-card">
                <div className="auth-logo">
                    <Link to="/" className="cursor-pointer">
                        <img 
                            src="https://customer-assets-agu9un31.emergentagent.net/job_fleet-ops-62/artifacts/sxi4lya9_%CE%91%CE%BD%CF%89%CC%81%CE%BD%CF%85%CE%BC%CE%BF%20%CF%83%CF%87%CE%B5%CC%81%CE%B4%CE%B9%CE%BF.webp" 
                            alt="Cofleet Logo" 
                            style={{ width: '220px', height: 'auto' }}
                            className="object-contain mx-auto mb-4 hover:opacity-80 transition-opacity"
                        />
                    </Link>
                    <p className="auth-subtitle">{language === 'el' ? 'Συνδεθείτε για να διαχειριστείτε τον στόλο σας' : 'Sign in to manage your fleet'}</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="form-group">
                        <Label htmlFor="email" className="form-label">{t('auth.email')}</Label>
                        <Input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="name@company.com"
                            className="bg-white border-slate-300 text-slate-900"
                            required
                            data-testid="login-email-input"
                        />
                    </div>

                    <div className="form-group">
                        <Label htmlFor="password" className="form-label">{t('auth.password')}</Label>
                        <div className="relative">
                            <Input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder={language === 'el' ? 'Εισάγετε τον κωδικό σας' : 'Enter your password'}
                                className="bg-white border-slate-300 text-slate-900 pr-10"
                                required
                                data-testid="login-password-input"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                                data-testid="toggle-password-visibility"
                            >
                                {showPassword ? (
                                    <EyeOff className="w-5 h-5" />
                                ) : (
                                    <Eye className="w-5 h-5" />
                                )}
                            </button>
                        </div>
                    </div>

                    <Button
                        type="submit"
                        className="w-full btn-primary h-11"
                        disabled={loading}
                        data-testid="login-submit-btn"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                {language === 'el' ? 'Σύνδεση...' : 'Signing in...'}
                            </>
                        ) : (
                            t('landing.signIn')
                        )}
                    </Button>

                    <div className="text-center">
                        <Link
                            to="/forgot-password"
                            className="text-sm text-[#1B7EB5] hover:text-[#58A6B4] transition-colors"
                            data-testid="forgot-password-link"
                        >
                            {language === 'el' ? 'Ξέχασα τον κωδικό μου' : 'Forgot my password'}
                        </Link>
                    </div>
                </form>

                <div className="mt-6 text-center">
                    <p className="text-sm text-zinc-500">
                        {t('auth.dontHaveAccount')}{' '}
                        <Link 
                            to="/register" 
                            className="text-[#8CC774] hover:text-[#58A6B4] transition-colors"
                            data-testid="register-link"
                        >
                            {t('auth.createAccount')}
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
